import type { ComponentProps } from 'react';

import { Icon } from '../../components';
import { cn, getAccessibleTextColor } from '../../lib/utils';

type RatingToggleProps = Omit<ComponentProps<'button'>, 'color'> & {
  color: string;
  selected?: boolean;
};

function RatingToggle({
  color,
  selected = false,
  className,
  onClick,
  children,
  ...props
}: RatingToggleProps) {
  const textColor = getAccessibleTextColor(color);
  const isInteractive = !!onClick;

  return (
    <button
      data-slot={'rating-toggle'}
      type={'button'}
      aria-pressed={selected}
      className={cn(
        'relative flex flex-col items-center justify-center h-[46px] rounded-lg',
        'border border-solid border-transparent',
        'hover:opacity-90',
        'transition-all duration-200 ease-out',
        selected && 'border-primary-active',
        isInteractive && 'cursor-pointer',
        className
      )}
      style={{ backgroundColor: color, color: textColor }}
      onClick={onClick}
      {...props}
    >
      {children}
      {selected && <RatingToggleCheck />}
    </button>
  );
}

function RatingToggleLabel({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-label'}
      className={cn('text-sm font-semibold whitespace-nowrap', className)}
      {...props}
    />
  );
}

function RatingToggleValue({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-value'}
      className={cn('text-sm font-semibold', className)}
      {...props}
    />
  );
}

function RatingToggleCheck({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-check'}
      className={cn(
        'absolute top-1 right-1 flex items-center justify-center size-5 rounded-full bg-primary-active',
        className
      )}
      {...props}
    >
      <Icon name={'check'} size={'xs'} />
    </span>
  );
}

RatingToggle.Label = RatingToggleLabel;
RatingToggle.Value = RatingToggleValue;
RatingToggle.Check = RatingToggleCheck;

export { RatingToggle };
export type { RatingToggleProps };
